import { useParams, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PreferEarlierTick from '@/components/booking/PreferEarlierTick';
import HostedPaymentMock from '@/components/HostedPaymentMock';
import { addDays, format } from 'date-fns';
import {
  selfBookStartsForDay,
  slotStartIso,
  type SelfBookSession,
  type SelfBookStart,
} from '@/domain/selfBookFlow';
import { SESSION_META } from '@/domain/pricing';
import {
  fetchPublicSelfBook,
  payPublicSelfBook,
  type PublicSelfBookBundle,
  type SelfBookConfirmation,
} from '@/api/publicSelfBook';
import { CheckCircle2, ImagePlus, X } from 'lucide-react';

type Stage = 'session' | 'slot' | 'details' | 'design' | 'pay';

const SESSIONS: SelfBookSession[] = ['half-day', 'full-day'];

// The customer always sees the real price (never the staff money mask).
const aud = (amount: number): string =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);

const SLOT_TAKEN_COPY = 'That time was just taken — pick another';

/** One idempotency key per checkout attempt: a retry of the SAME attempt replays
 *  on the server (never a second charge); a new choice mints a new one. */
function newAttemptId(): string {
  return crypto.randomUUID();
}

/**
 * Customer-facing self-book page. PUBLIC + server-driven (tv-2na.8.4): it
 * renders with NO staff login, so it loads the link by its URL token and pays
 * through /api/public/self-book/:token. The server decides everything that
 * matters — which link, the price (the pinned hold, else the artist's rate
 * card), which times are free (firm bookings only) and whether the pay landed —
 * and the page only shows success once the server has said ok.
 */
export default function SelfBookPage() {
  const { linkToken } = useParams<{ linkToken: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const receipt = searchParams.get('receipt') ?? undefined;

  // undefined = loading; null = unknown link.
  const [bundle, setBundle] = useState<PublicSelfBookBundle | null | undefined>(undefined);
  const [stage, setStage] = useState<Stage>('session');
  const [session, setSession] = useState<SelfBookSession | null>(null);
  const [pickedDate, setPickedDate] = useState<Date | null>(null);
  const [pickedStart, setPickedStart] = useState<SelfBookStart | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [designBrief, setDesignBrief] = useState('');
  const [referencePhotos, setReferencePhotos] = useState<string[]>([]);
  // B7 (8xat D08-09; Anas 1 Oct Q5): the client's own "Happy to come in earlier"
  // tick. Starts as the staff who made the link set it; null = untouched.
  const [preferEarlier, setPreferEarlier] = useState<boolean | null>(null);
  const [confirmed, setConfirmed] = useState<SelfBookConfirmation | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [priceOverride, setPriceOverride] = useState<number | null>(null);
  const [payAttempt, setPayAttempt] = useState(0); // bump remounts HostedPaymentMock to idle
  const attemptId = useRef(newAttemptId());
  const seededPin = useRef(false);

  const load = useCallback(async () => {
    if (!linkToken) {
      setBundle(null);
      return null;
    }
    try {
      const b = await fetchPublicSelfBook(linkToken, receipt ? { receipt } : {});
      setBundle(b);
      if (b?.confirmed) setConfirmed(b.confirmed);
      return b;
    } catch {
      setBundle(null);
      return null;
    }
  }, [linkToken, receipt]);

  useEffect(() => {
    void load();
  }, [load]);

  // A pinned link (the artist set the date + time) opens straight at the details
  // step with the pinned slot preselected — decided on the FIRST loaded bundle.
  useEffect(() => {
    const pin = bundle?.link.pinned;
    if (!pin || seededPin.current) return;
    seededPin.current = true;
    const start = new Date(pin.startIso);
    setSession(pin.sessionLength);
    setPickedDate(start);
    setPickedStart(format(start, 'HH:mm') as SelfBookStart);
    setStage('details');
  }, [bundle]);

  // D05-11: a named link pre-fills what the studio already holds for its own
  // customer (still editable); a universal link is anyone's and pre-fills nothing.
  const prefill = bundle?.prefill ?? null;
  useEffect(() => {
    if (!prefill) return;
    setName((n) => n || prefill.name);
    setEmail((e) => e || prefill.email);
    setMobile((m) => m || prefill.mobile);
  }, [prefill]);

  useEffect(() => {
    if (confirmed && typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [confirmed]);

  const artist = bundle?.artist;
  const pinned = bundle?.link.pinned ?? null;

  const startsFor = useCallback(
    (day: Date, s: SelfBookSession) =>
      bundle
        ? selfBookStartsForDay({
            availability: bundle.artist.availability,
            artistId: bundle.artist.id,
            day,
            session: s,
            busy: bundle.busy,
          })
        : [],
    [bundle],
  );

  // Next fortnight of candidate days that offer at least one start for the chosen
  // session. Day-off, the night rule and FIRM bookings all fold into this.
  const dayOptions = useMemo(() => {
    if (!bundle || !session) return [];
    const rangeEnd = bundle.link.dateRangeEnd ? new Date(bundle.link.dateRangeEnd) : null;
    return Array.from({ length: 14 }, (_, i) => addDays(new Date(bundle.nowIso), i + 2))
      .filter((day) => !rangeEnd || day <= rangeEnd)
      .map((day) => ({ day, starts: startsFor(day, session) }))
      .filter((d) => d.starts.length > 0);
  }, [bundle, session, startsFor]);

  const startsForPicked = useMemo(
    () => (session && pickedDate ? startsFor(pickedDate, session) : ([] as SelfBookStart[])),
    [session, pickedDate, startsFor],
  );

  if (bundle === undefined) {
    return (
      <CustomerShell>
        <p className="font-serif text-cream-soft text-center" data-testid="self-book-loading">
          Loading your booking link…
        </p>
      </CustomerShell>
    );
  }

  if (bundle === null) {
    return (
      <CustomerShell>
        <p className="font-serif text-cream-soft text-center" data-testid="self-book-missing">
          This link has expired or was not generated yet. Get in touch with the studio.
        </p>
      </CustomerShell>
    );
  }

  // ---- CONFIRMED (the server said ok, or a reload of a paid link) -----------
  if (confirmed) {
    const when = new Date(confirmed.startIso);
    return (
      <CustomerShell>
        <div className="text-center py-8" data-testid="self-book-success">
          <CheckCircle2 size={48} className="text-mint mx-auto" />
          <h1 className="heading-display text-2xl text-cream mt-4">You&apos;re booked</h1>
          <p className="font-serif text-cream-soft mt-2">
            Your booking with {confirmed.artistFirstName} is confirmed for {format(when, 'EEEE d MMMM')} at{' '}
            {format(when, 'HH:mm')}.
          </p>
          <p className="font-inter text-[11px] text-cream-soft mt-6 border border-gold-dim/30 rounded-md px-3 py-2">
            Deposit paid: <span className="text-gold-bright">{aud(confirmed.amountAud)}</span>. A confirmation
            text is on its way.
          </p>
        </div>
      </CustomerShell>
    );
  }

  if (bundle.link.state !== 'open') {
    return (
      <CustomerShell>
        <p className="font-serif text-cream-soft text-center" data-testid="self-book-expired">
          {bundle.link.state === 'used'
            ? 'This link has already been used. Get in touch with the studio to book another session.'
            : 'This link has expired. Get in touch with the studio to book a new session.'}
        </p>
      </CustomerShell>
    );
  }

  // The price the server will charge: the pinned hold, else the rate card.
  const deposit = priceOverride ?? (pinned ? pinned.amountAud : session ? bundle.prices[session] : 0);
  const detailsComplete = name.trim().length > 0 && email.trim().length > 0 && mobile.trim().length > 0;
  const designComplete = designBrief.trim().length > 0 && referencePhotos.length >= 1;

  const settle = async (): Promise<boolean> => {
    if (!linkToken || !session || !pickedDate || !pickedStart) return false;
    setPayError(null);
    const startIso = pinned ? pinned.startIso : slotStartIso(pickedDate, pickedStart);
    const res = await payPublicSelfBook(linkToken, {
      attemptId: attemptId.current,
      startIso,
      ...(pinned ? {} : { session }),
      details: { name: name.trim(), email: email.trim(), mobile: mobile.trim() },
      designBrief: designBrief.trim(),
      referencePhotos,
      quotedAmountAud: deposit,
      preferBroughtForward: preferEarlier ?? bundle.link.preferBroughtForward === true,
    });
    if (res.ok) {
      setConfirmed({ startIso: res.startIso, amountAud: res.amountAud, artistFirstName: res.artistFirstName });
      // A reload of this URL shows the confirmation, never a second pay form.
      setSearchParams({ receipt: res.receiptId }, { replace: true });
      return true;
    }
    const transient = res.status === 0 || res.status >= 500 || res.status === 429;
    // A definitive refusal ends this attempt; a transient failure keeps the id so
    // the retry replays safely on the server.
    if (!transient) attemptId.current = newAttemptId();
    if (res.code === 'slot_taken') {
      setPayError(SLOT_TAKEN_COPY);
      if (!pinned) {
        setPickedDate(null);
        setPickedStart(null);
        setStage('slot');
      }
      await load();
    } else if (res.code === 'price_changed' && typeof res.amountAud === 'number') {
      setPriceOverride(res.amountAud);
      setPayError(`The deposit for this session is now ${aud(res.amountAud)}. Nothing was charged — please review and pay again.`);
    } else if (res.code === 'link_used' || res.status === 410 || res.code === 'pin_changed') {
      await load();
      setPayError('This link has changed since you opened it. Nothing was charged.');
    } else {
      setPayError("Your payment didn't go through — no charge was made. Please try again.");
    }
    setPayAttempt((n) => n + 1);
    return false;
  };

  // ---- PAY -----------------------------------------------------------------
  if (stage === 'pay' && session && pickedDate && pickedStart) {
    return (
      <CustomerShell>
        <h1 className="heading-display text-2xl text-cream" data-testid="self-book-amount">
          {aud(deposit)} deposit
        </h1>
        <p className="font-serif text-cream-soft mt-2">
          {name ? `${name.split(' ')[0]}, ` : ''}you&apos;re locking in {format(pickedDate, 'EEEE d MMMM')} at{' '}
          {pickedStart} with {artist?.displayName}.
        </p>
        <div className="mt-6">
          {payError ? (
            <p
              className="mb-3 rounded-md border border-blood/50 bg-blood/10 p-3 text-center font-serif text-sm text-cream"
              data-testid="self-book-pay-error"
            >
              {payError}
            </p>
          ) : null}
          <HostedPaymentMock
            key={payAttempt}
            amountAud={deposit}
            description={`${SESSION_META[session].label} session with ${artist?.displayName}.`}
            testid="customer-hosted-payment"
            settle={settle}
            onConfirm={() => {}}
          />
        </div>
        <button
          type="button"
          onClick={() => setStage('design')}
          className="font-mono text-xs text-cream-soft mt-4 underline"
        >
          Back to details
        </button>
      </CustomerShell>
    );
  }

  // ---- WIZARD (session → slot → details → design) --------------------------
  return (
    <CustomerShell>
      <header>
        {artist?.avatarUrl ? (
          <img
            src={artist.avatarUrl}
            alt={artist.displayName}
            className="h-16 w-16 rounded-full mx-auto mb-4 object-cover border border-gold-bright/60"
          />
        ) : null}
        <p className="label-small-caps text-gold-bright text-center">Book your session</p>
        <h1 className="heading-display text-3xl text-gold-bright text-center mt-1">
          with {artist?.displayName}
        </h1>
      </header>

      {/* 1 - SESSION (half / full only, no size) */}
      {stage === 'session' ? (
        <section className="mt-6">
          <p className="label-small-caps text-gold-bright mb-3">Choose length</p>
          <div className="space-y-2">
            {SESSIONS.map((s) => {
              const selected = s === session;
              return (
                <button
                  key={s}
                  type="button"
                  data-testid={`session-${s}`}
                  onClick={() => setSession(s)}
                  className={
                    selected
                      ? 'w-full flex items-center justify-between rounded-md border border-gold-bright bg-gold/10 px-4 py-3'
                      : 'w-full flex items-center justify-between rounded-md border border-gold-dim/30 px-4 py-3 hover:border-gold-bright/60'
                  }
                >
                  <span className="text-left">
                    <span className="font-inter text-sm text-cream block">{SESSION_META[s].label}</span>
                    <span className="font-inter text-[10px] text-cream-soft uppercase tracking-widest">
                      {SESSION_META[s].description}
                    </span>
                  </span>
                  <span className="font-mono text-sm text-gold-bright">{aud(bundle.prices[s])} deposit</span>
                </button>
              );
            })}
          </div>
          <p className="font-serif text-cream-soft text-xs mt-3">
            No size to choose - {artist?.displayName} works that out with you on the day.
          </p>
          {session ? (
            <button
              type="button"
              onClick={() => setStage('slot')}
              className="btn-primary mt-6 w-full"
              data-testid="session-continue"
            >
              Continue
            </button>
          ) : null}
        </section>
      ) : null}

      {/* 2 - DAY + START (11:00 / 15:00 from real availability) */}
      {stage === 'slot' && session ? (
        <section className="mt-6">
          {payError ? (
            <p
              className="mb-3 rounded-md border border-blood/50 bg-blood/10 p-3 text-center font-serif text-sm text-cream"
              data-testid="self-book-pay-error"
            >
              {payError}
            </p>
          ) : null}
          <p className="label-small-caps text-gold-bright mb-3">Pick a day</p>
          {dayOptions.length === 0 ? (
            <p className="font-serif text-cream-soft text-sm" data-testid="self-book-no-days">
              {artist?.displayName} is fully booked for the next two weeks. Get in touch with the studio and
              we&apos;ll find you a time.
            </p>
          ) : null}
          <div className="grid grid-cols-4 gap-2">
            {dayOptions.map(({ day }, i) => {
              const selected = pickedDate && day.toDateString() === pickedDate.toDateString();
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => {
                    setPickedDate(day);
                    setPickedStart(null);
                    setPayError(null);
                  }}
                  className={
                    selected
                      ? 'rounded-md border border-gold-bright bg-gold/10 px-2 py-2 text-cream'
                      : 'rounded-md border border-gold-dim/30 px-2 py-2 text-cream-soft hover:border-gold-bright/60'
                  }
                  data-testid={`pick-day-${i}`}
                  data-day={format(day, 'yyyy-MM-dd')}
                >
                  <p className="font-inter text-[9px] uppercase tracking-widest">{format(day, 'EEE')}</p>
                  <p className="font-inter text-base mt-0.5">{format(day, 'd')}</p>
                  <p className="font-mono text-[9px] text-cream-soft">{format(day, 'MMM')}</p>
                </button>
              );
            })}
          </div>

          {pickedDate ? (
            <>
              <p className="label-small-caps text-gold-bright mb-3 mt-6">Pick a start</p>
              <div className="grid grid-cols-2 gap-2">
                {startsForPicked.map((start) => {
                  const selected = start === pickedStart;
                  return (
                    <button
                      key={start}
                      type="button"
                      onClick={() => setPickedStart(start)}
                      data-testid={`start-${start}`}
                      className={
                        selected
                          ? 'rounded-md border border-gold-bright bg-gold/10 px-2 py-2 font-mono text-sm text-cream'
                          : 'rounded-md border border-gold-dim/30 px-2 py-2 font-mono text-sm text-cream-soft hover:border-gold-bright/60'
                      }
                    >
                      {start}
                    </button>
                  );
                })}
              </div>
              <p className="font-serif text-cream-soft text-xs mt-3">
                {session === 'full-day'
                  ? 'A full day runs about eight hours, so start times are limited to what fits the artist’s day.'
                  : 'A half day runs about four hours.'}
              </p>
            </>
          ) : null}

          {pickedDate && pickedStart ? (
            <button
              type="button"
              onClick={() => setStage('details')}
              className="btn-primary mt-6 w-full"
              data-testid="slot-continue"
            >
              Continue
            </button>
          ) : null}
        </section>
      ) : null}

      {/* 3 - YOUR DETAILS (name / email / mobile) */}
      {stage === 'details' ? (
        <section className="mt-6">
          <p className="label-small-caps text-gold-bright mb-3">Your details</p>
          {pinned ? (
            <p className="font-serif text-cream-soft text-xs mb-3" data-testid="pinned-slot-summary">
              {artist?.displayName} has held {format(new Date(pinned.startIso), 'EEEE d MMMM')} at{' '}
              {format(new Date(pinned.startIso), 'HH:mm')} for you. Just add your details and pay.
            </p>
          ) : null}
          <div className="space-y-3">
            <Field label="Full name">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="detail-name"
                className="w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="detail-email"
                className="w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
              />
            </Field>
            <Field label="Mobile">
              <input
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                data-testid="detail-mobile"
                className="w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
              />
            </Field>
          </div>
          <p className="font-serif text-cream-soft text-xs mt-3">
            We use these to confirm your booking and send reminders.
          </p>
          <button
            type="button"
            disabled={!detailsComplete}
            onClick={() => setStage('design')}
            className="btn-primary mt-6 w-full disabled:opacity-50"
            data-testid="details-continue"
          >
            Continue
          </button>
        </section>
      ) : null}

      {/* 4 - DESIGN BRIEF (description + >=1 reference image, required) */}
      {stage === 'design' ? (
        <section className="mt-6">
          <p className="label-small-caps text-gold-bright mb-3">Your idea</p>
          <textarea
            value={designBrief}
            onChange={(e) => setDesignBrief(e.target.value)}
            placeholder={`Describe the piece for ${artist?.displayName}…`}
            data-testid="design-brief"
            className="w-full min-h-[90px] bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
          />
          <p className="label-small-caps text-gold-bright mt-4 mb-2">
            Reference images <span className="text-blood">· at least one</span>
          </p>
          <div className="flex gap-2 flex-wrap">
            {referencePhotos.map((src, i) => (
              <div
                key={src}
                className="relative h-16 w-16 rounded-md border border-gold-dim/40 bg-ink-dark grid place-items-center"
              >
                <span className="font-mono text-[9px] text-cream-soft">ref {i + 1}</span>
                <button
                  type="button"
                  onClick={() => setReferencePhotos((p) => p.filter((_, j) => j !== i))}
                  className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-ink-black border border-gold-dim/40 grid place-items-center"
                  aria-label={`Remove reference ${i + 1}`}
                >
                  <X size={11} className="text-cream-soft" />
                </button>
              </div>
            ))}
            <button
              type="button"
              data-testid="add-reference"
              onClick={() =>
                setReferencePhotos((p) => [...p, `ref://upload-${p.length + 1}.jpg`])
              }
              className="h-16 w-16 rounded-md border border-dashed border-blood/70 grid place-items-center text-blood"
            >
              <ImagePlus size={18} />
            </button>
          </div>
          <PreferEarlierTick
            checked={preferEarlier ?? bundle.link.preferBroughtForward === true}
            onChange={setPreferEarlier}
            className="mt-5"
          />
          <button
            type="button"
            disabled={!designComplete}
            onClick={() => setStage('pay')}
            className="btn-primary mt-6 w-full disabled:opacity-50"
            data-testid="design-continue"
          >
            Continue to {aud(deposit)} deposit
          </button>
          {!designComplete ? (
            <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest text-center mt-3">
              Add a description and one reference to continue
            </p>
          ) : null}
        </section>
      ) : null}
    </CustomerShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="font-inter text-[10px] text-cream-soft uppercase tracking-widest">{label}</span>
      <span className="block mt-1">{children}</span>
    </label>
  );
}

export function CustomerShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen w-full bg-ink-black text-cream flex flex-col items-center px-6 py-8">
      <div className="max-w-sm w-full">
        <p className="font-cinzel-deco text-xs text-gold-bright tracking-widest text-center mb-6">
          TATTOO VAULT
        </p>
        {children}
        <p className="font-inter text-[9px] text-cream-soft/70 text-center mt-10 uppercase tracking-widest">
          Test mode. Mock-everything. No real money moves.
        </p>
      </div>
    </div>
  );
}
